/**
 * useIdeaDetail Hook
 * 
 * Fetches and manages a single idea with comments and activity.
 * Provides actions for voting, commenting, and updating.
 * Encapsulates all API calls per Rule 6.
 */

import { useState, useEffect, useCallback } from 'react';
import { authApi } from '../shared/utils/api';
import { logger } from '../shared/utils/logger';
import {
  IdeaWithDetails,
  IdeaComment,
  IdeaActivity,
  UpdateIdeaRequest,
  VoteType,
} from '../shared/types/idea';

interface UseIdeaDetailProps {
  ideaId: number;
}

interface UseIdeaDetailReturn {
  idea: IdeaWithDetails | null;
  comments: IdeaComment[];
  activities: IdeaActivity[];
  loading: boolean;
  updating: boolean;
  voting: boolean;
  commenting: boolean;
  loadingComments: boolean;
  loadingActivity: boolean;
  error: string | null;
  updateIdea: (data: UpdateIdeaRequest) => Promise<boolean>;
  deleteIdea: () => Promise<boolean>;
  voteIdea: (voteType: VoteType | null) => Promise<boolean>;
  addComment: (content: string, parentId?: number) => Promise<boolean>;
  refresh: () => Promise<void>;
  refreshComments: () => Promise<void>;
  refreshActivity: () => Promise<void>;
}

export function useIdeaDetail({ ideaId }: UseIdeaDetailProps): UseIdeaDetailReturn {
  const [idea, setIdea] = useState<IdeaWithDetails | null>(null);
  const [comments, setComments] = useState<IdeaComment[]>([]);
  const [activities, setActivities] = useState<IdeaActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [voting, setVoting] = useState(false);
  const [commenting, setCommenting] = useState(false);
  const [loadingComments, setLoadingComments] = useState(true);
  const [loadingActivity, setLoadingActivity] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchIdea = useCallback(async () => {
    if (!ideaId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await authApi.getIdea(ideaId);
      if (response.success && response.idea) {
        setIdea(response.idea);
      } else {
        setError(response.message || 'Failed to load idea');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load idea';
      logger.error('Failed to fetch idea', err, 'useIdeaDetail');
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [ideaId]);

  const fetchComments = useCallback(async () => {
    if (!ideaId) return;
    setLoadingComments(true);
    try {
      const response = await authApi.getIdeaComments(ideaId);
      if (response.success) {
        setComments(response.comments);
      }
    } catch (err: unknown) {
      logger.error('Failed to fetch comments', err, 'useIdeaDetail');
    } finally {
      setLoadingComments(false);
    }
  }, [ideaId]);

  const fetchActivity = useCallback(async () => {
    if (!ideaId) return;
    setLoadingActivity(true);
    try {
      const response = await authApi.getIdeaActivity(ideaId);
      if (response.success) {
        setActivities(response.activities);
      }
    } catch (err: unknown) {
      logger.error('Failed to fetch activity', err, 'useIdeaDetail');
    } finally {
      setLoadingActivity(false);
    }
  }, [ideaId]);

  useEffect(() => {
    fetchIdea();
    fetchComments();
    fetchActivity();
  }, [fetchIdea, fetchComments, fetchActivity]);

  const updateIdea = useCallback(async (data: UpdateIdeaRequest): Promise<boolean> => {
    setUpdating(true);
    try {
      const response = await authApi.updateIdea(ideaId, data);
      if (response.success && response.idea) {
        setIdea(response.idea);
        fetchActivity();
        return true;
      } else {
        setError(response.message || 'Failed to update idea');
        return false;
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update idea';
      logger.error('Update idea failed', err, 'useIdeaDetail');
      setError(message);
      return false;
    } finally {
      setUpdating(false);
    }
  }, [ideaId, fetchActivity]);

  const deleteIdea = useCallback(async (): Promise<boolean> => {
    try {
      const response = await authApi.deleteIdea(ideaId);
      if (response.success) {
        return true;
      } else {
        setError(response.message || 'Failed to delete idea');
        return false;
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to delete idea';
      logger.error('Delete idea failed', err, 'useIdeaDetail');
      setError(message);
      return false;
    }
  }, [ideaId]);

  const voteIdea = useCallback(async (voteType: VoteType | null): Promise<boolean> => {
    setVoting(true);
    try {
      const response = await authApi.voteIdea(ideaId, { vote_type: voteType });
      if (response.success && idea) {
        setIdea({ ...idea, upvotes: response.upvotes, downvotes: response.downvotes, score: response.score, user_vote: voteType });
        fetchActivity();
        return true;
      } else {
        setError(response.message || 'Failed to vote');
        return false;
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to vote';
      logger.error('Vote failed', err, 'useIdeaDetail');
      setError(message);
      return false;
    } finally {
      setVoting(false);
    }
  }, [ideaId, idea, fetchActivity]);

  const addComment = useCallback(async (content: string, parentId?: number): Promise<boolean> => {
    setCommenting(true);
    try {
      const response = await authApi.addIdeaComment(ideaId, { content, parent_id: parentId });
      if (response.success && response.comment) {
        setComments(prev => [...prev, response.comment]);
        if (idea) {
          setIdea({ ...idea, comment_count: idea.comment_count + 1 });
        }
        fetchActivity();
        return true;
      } else {
        setError(response.message || 'Failed to add comment');
        return false;
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to add comment';
      logger.error('Add comment failed', err, 'useIdeaDetail');
      setError(message);
      return false;
    } finally {
      setCommenting(false);
    }
  }, [ideaId, idea, fetchActivity]);

  return {
    idea, comments, activities, loading, updating, voting, commenting, loadingComments, loadingActivity, error,
    updateIdea, deleteIdea, voteIdea, addComment,
    refresh: fetchIdea, refreshComments: fetchComments, refreshActivity: fetchActivity,
  };
}
